import { z } from 'zod';

import {
  CheckQuestion,
  CheckQuestionResponse,
  isCheckQuestion,
  isCheckResponse,
  QuestionResponse,
  TypedQuestion
} from 'types/question-response.types';

import { QuestionResponseFormValue, IsRequired, OTHER_OPTION } from '../base.types';

type BaseCheckQuestionResponseFormValue = QuestionResponseFormValue<
  CheckQuestionResponse,
  'selectedOptions' | 'otherValue'
>;

type NonEmptyCheckQuestionResponseFormValue = BaseCheckQuestionResponseFormValue &
  (
    | { selectedOptions: [string, ...string[]]; otherValue: undefined }
    | { selectedOptions: [string, ...string[]]; otherValue: string }
  );
export type FormValue = BaseCheckQuestionResponseFormValue | NonEmptyCheckQuestionResponseFormValue;

export const valueFromFormValue = (response: FormValue): string[] | undefined => {
  if (!isNonEmpty(response)) return undefined;
  const selectedOptions = response.selectedOptions.filter((v) => v !== OTHER_OPTION);
  return response.otherValue ? [...selectedOptions, response.otherValue] : selectedOptions;
};

export const isNonEmpty = (
  response: FormValue
): response is NonEmptyCheckQuestionResponseFormValue => {
  const { selectedOptions } = response;
  if (!selectedOptions) return false;
  const withoutOtherValue = selectedOptions.filter((v) => v !== OTHER_OPTION);
  if (withoutOtherValue.length > 0) return true;
  if ('otherValue' in response && !!response.otherValue) {
    return response.otherValue.length > 0;
  } else {
    return false;
  }
};

export const submissionValue = (
  response: NonEmptyCheckQuestionResponseFormValue
): Omit<CheckQuestionResponse, 'id'> => {
  const { type, selectedOptions } = response;
  const base = { type };
  const otherValue = 'otherValue' in response ? response.otherValue : null;
  const isOtherSelected = selectedOptions.includes(OTHER_OPTION);
  const inOptions = selectedOptions.filter((v) => v !== OTHER_OPTION);
  return isOtherSelected && !!otherValue
    ? { ...base, otherValue, selectedOptions: inOptions }
    : { ...base, otherValue: null, selectedOptions: inOptions as [string, ...string[]] };
};

export const schema = ({ options, hasOtherOption, type }: CheckQuestion): z.ZodType<FormValue> => {
  const opts = hasOtherOption ? [...options, OTHER_OPTION] : options;
  const isAllowedAnswer = (values: string[]): boolean => {
    return values.every((selectedOptions) => opts.includes(selectedOptions));
  };
  const baseSchema = z.object({ type: z.literal(type) });
  const valueSchema = z.discriminatedUnion('req', [
    z.object({
      selectedOptions: z
        .array(z.string())
        .nonempty('This question is required.')
        .refine(isAllowedAnswer),
      req: z.literal(IsRequired.Req)
    }),
    z.object({
      selectedOptions: z.array(z.string()).refine(isAllowedAnswer),
      req: z.literal(IsRequired.Opt)
    })
  ]);
  if (hasOtherOption) {
    return baseSchema
      .extend({ otherValue: z.string().optional() })
      .and(valueSchema)
      .superRefine((data, ctx) => {
        if (data.selectedOptions?.includes(OTHER_OPTION)) {
          const { otherValue } = data;
          const isEmpty = otherValue === undefined || otherValue === null || otherValue === '';
          if (isEmpty) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'This field is required.',
              path: ['otherValue']
            });
          }
        }
      });
  } else {
    return baseSchema.and(valueSchema);
  }
};

export const initialize: (
  question: CheckQuestion,
  response?: CheckQuestionResponse
) => FormValue = ({ type, isRequired: required, options, hasOtherOption }, response) => {
  const req = required ? IsRequired.Req : IsRequired.Opt;
  const base = { type, req };
  if (!response) {
    return { ...base, selectedOptions: [] };
  }
  const { selectedOptions, otherValue } = response;

  const inOptions = selectedOptions.filter((r) => options.includes(r));
  if (!hasOtherOption || otherValue === null) {
    return { ...base, selectedOptions: inOptions };
  }

  return { ...base, selectedOptions: [...inOptions, OTHER_OPTION], otherValue };
};

export const validateAndInitialize = (
  question: TypedQuestion,
  response?: QuestionResponse
): FormValue | undefined => {
  if (!isCheckQuestion(question)) return undefined;
  if (response && isCheckResponse(response)) return initialize(question, response);
  return initialize(question);
};

export const validateAndSchematize = (
  question: TypedQuestion
): z.ZodType<FormValue> | undefined => {
  return isCheckQuestion(question) ? schema(question) : undefined;
};

export const validateAndSerialize = (
  id: number,
  formValue: FormValue
): QuestionResponse | undefined => {
  if (!isNonEmpty(formValue)) return undefined;
  return { id, ...submissionValue(formValue) };
};
